import { actionTypes, buildingData, researchData, shipData, defenseData, calcProduction } from '../data';
import { addResources, timesResources } from '../resources';

// Returns whichever of this planet's in-progress actions (its own build
// slot, ship queue head, incoming fleet head) or the *global* research
// action (if it belongs to this planet) will complete soonest.
export const NextActionReady = (state, planetId) => {
  let fastestAction;
  const planet = state.planets[planetId];

  if (planet.buildAction !== undefined) {
    if (fastestAction === undefined || fastestAction.duration > planet.buildAction.duration) {
      fastestAction = planet.buildAction;
    }
  }
  if (planet.shipActions.length > 0) {
    if (fastestAction === undefined || fastestAction.duration > planet.shipActions[0].duration) {
      fastestAction = planet.shipActions[0];
    }
  }
  if (planet.fleetActions.length > 0) {
    if (fastestAction === undefined || fastestAction.duration > planet.fleetActions[0].duration) {
      fastestAction = planet.fleetActions[0];
    }
  }
  // eslint-disable-next-line eqeqeq
  if (state.researchAction !== undefined && state.researchAction.payload.planetId == planetId) {
    if (fastestAction === undefined || fastestAction.duration > state.researchAction.duration) {
      fastestAction = state.researchAction;
    }
  }
  return fastestAction;
};

// Advances `planetId`'s clock by `time`, accruing production and
// decrementing every in-progress action's remaining duration accordingly.
// `recorder` is required (not optional): the legacy engine unconditionally
// logged a plot point on every forward step, and this preserves that.
export const StateFastForward = (state, planetId, time, recorder) => {
  const planet = state.planets[planetId];
  const producedResources = timesResources(calcProduction(state, planetId), time);

  if (planet.buildAction !== undefined) {
    planet.buildAction.duration -= time;
  }
  for (const fleet of planet.fleetActions) {
    fleet.duration -= time;
  }
  if (planet.shipActions.length > 0) {
    if (planet.shipActions[0].duration < time) {
      console.log('ERROR: SHIP WAS IGNORED!');
    }
    planet.shipActions[0].duration -= time;
  }
  // Fixed vs. legacy: was `state.researchAction.planetId`, a field that
  // never existed (the planet id lives at payload.planetId), so this
  // decrement was dead code and research ran serially instead of in
  // parallel with other same-planet activity. See plan.md §4/§9.
  // eslint-disable-next-line eqeqeq
  if (state.researchAction !== undefined && state.researchAction.payload.planetId == planetId) {
    state.researchAction.duration -= time;
  }

  planet.time += time;
  planet.resources = addResources(planet.resources, producedResources);

  const hours = Math.floor(time);
  const minutes = Math.floor((time - hours) * 60);
  recorder.record(state, planetId, `Resources accumulated over ${hours}h ${minutes}m`);

  if (planet.time > state.time) {
    state.time = planet.time;
  }
  return state;
};

// Applies the completion effects of `action` (the action returned by
// NextActionReady) and logs a completion plot point.
export const CompleteAction = (state, action, recorder) => {
  const { type, payload } = action;
  switch (type) {
    case actionTypes.UPGRADE_BUILDING: {
      const { planetId, building } = payload;
      state.planets[planetId].buildings[building] += 1;
      state.planets[planetId].buildAction = undefined;
      recorder.record(state, planetId, `Completed ${buildingData[building].name} level ${state.planets[planetId].buildings[building]}`);
      break;
    }
    case actionTypes.RESEARCH: {
      const { planetId, tech } = payload;
      state.research[tech] += 1;
      state.researchAction = undefined;
      recorder.record(state, planetId, `Completed ${researchData[tech].name} level ${state.research[tech]}`);
      break;
    }
    case actionTypes.BUILD_SHIP: {
      const { planetId, ship } = payload;
      state.planets[planetId].ships[ship] += 1;
      state.planets[planetId].shipActions.shift();
      recorder.record(state, planetId, `Built ${shipData[ship].name}`);
      break;
    }
    case actionTypes.BUILD_DEFENSE: {
      const { planetId, defense } = payload;
      state.planets[planetId].defenses[defense] += 1;
      state.planets[planetId].shipActions.shift();
      recorder.record(state, planetId, `Built ${defenseData[defense].name}`);
      break;
    }
    case actionTypes.TRANSPORT: {
      const { planetId, settings } = payload;
      state.planets[settings.target].resources.metal += settings.metal;
      state.planets[settings.target].resources.crystal += settings.crystal;
      state.planets[settings.target].resources.deuterium += settings.deuterium;
      state.planets[settings.target].fleetActions.shift();
      recorder.record(state, settings.target, `Received transport from Planet ${planetId}`);
      break;
    }
    default:
      break;
  }
  return state;
};
